/**
 * 로그아웃 시 백엔드 리프레시 토큰 폐기 API를 먼저 호출하고,
 * 이후 NextAuth signOut을 수행하기 위한 공통 유틸입니다.
 */
import { signOut } from 'next-auth/react';
import { UNAUTH_LANDING_PAGE_URL } from '@/shared/routes';
import { createLoggedOutCookie } from '@/shared/auth/logout-state';

const LOGOUT_API_ENDPOINT = '/api/auth/logout';
const LOGOUT_TIMEOUT_MS = 5000;
let signOutRequest: Promise<Awaited<ReturnType<typeof signOut>>> | null = null;

/** 수동 로그아웃 중 발생한 인증 오류가 중복 종료와 만료 안내를 시작하지 않도록 합니다. */
export function isSignOutInProgress(): boolean {
  return signOutRequest !== null;
}

/**
 * 백엔드 로그아웃 API를 호출해 보유한 refresh token을 폐기하려고 시도합니다.
 */
async function revokeBackendRefreshToken(): Promise<void> {
  await fetch(LOGOUT_API_ENDPOINT, {
    cache: 'no-store',
    method: 'POST',
    signal: AbortSignal.timeout(LOGOUT_TIMEOUT_MS),
  }).catch(() => {
    // 로그아웃은 프런트 세션 정리가 우선이므로 실패를 전파하지 않습니다.
  });
}

/**
 * 백엔드 폐기를 시도한 뒤 세션 쿠키를 정리하고 새 문서로 이동합니다.
 * 동시 요청은 하나로 합쳐 로그아웃과 세션 만료 처리가 서로 경합하지 않게 합니다.
 */
async function performSignOut(options?: Parameters<typeof signOut>[0]) {
  await revokeBackendRefreshToken();
  // 진행 중이던 API 응답이 이전 쿠키를 다시 설정해도 종료된 세션을 사용하지 않습니다.
  document.cookie = createLoggedOutCookie(window.location.protocol === 'https:');
  const redirectTo = options?.redirectTo ?? options?.callbackUrl ?? UNAUTH_LANDING_PAGE_URL;
  const result = await signOut({ ...options, redirect: false, redirectTo });

  if (options?.redirect !== false) {
    // 인증 경계에서는 Router Cache를 재사용하지 않으며 외부 주소로 이동하지 않습니다.
    let safeTarget = new URL(UNAUTH_LANDING_PAGE_URL, window.location.origin);
    try {
      const target = new URL(redirectTo, window.location.origin);
      if (target.origin === window.location.origin && !target.username && !target.password) safeTarget = target;
    } catch {
      // 잘못된 콜백 주소가 세션 정리 후 로그인 문서 이동을 막지 않도록 합니다.
    }
    safeTarget.hash = '';
    window.location.replace(safeTarget.toString());
  }

  return result;
}

export function signOutWithRefreshRevoke(options?: Parameters<typeof signOut>[0]) {
  if (!signOutRequest) {
    signOutRequest = performSignOut(options).then(
      result => {
        // 문서 이동이 끝날 때까지 중복 종료를 막고, 이동 없는 호출만 다음 요청을 허용합니다.
        if (options?.redirect === false) signOutRequest = null;
        return result;
      },
      error => {
        signOutRequest = null;
        throw error;
      }
    );
  }
  return signOutRequest;
}
